import type { Screen0LanguageCode } from "@/features/language/language-registry";

/** iTunes Lookup API `country` param (ISO 3166-1 alpha-2 storefront). */
export type ItunesStorefrontCountry = string;

export const DEFAULT_APP_STORE_LOOKUP_COUNTRIES: readonly ItunesStorefrontCountry[] = [
  "us",
  "gb",
] as const;

/** Maps Screen0 UI language to a primary App Store storefront. */
export function screen0LanguageToItunesCountry(
  languageCode: Screen0LanguageCode,
): ItunesStorefrontCountry {
  if (languageCode === "en") return "us";
  return languageCode;
}

/**
 * Ordered storefront list for iTunes Lookup: user locale first, then common
 * fallbacks. Deduplicates while preserving order.
 */
export function buildAppStoreLookupCountries(
  languageCode?: Screen0LanguageCode | null,
): ItunesStorefrontCountry[] {
  const primary = languageCode
    ? screen0LanguageToItunesCountry(languageCode)
    : DEFAULT_APP_STORE_LOOKUP_COUNTRIES[0];

  const ordered = [primary, "gb", "us", "de", "fr", "es", "it"];
  const seen = new Set<string>();
  const result: string[] = [];

  for (const country of ordered) {
    const normalized = country.trim().toLowerCase();
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    result.push(normalized);
  }

  return result;
}

/** Legacy negative-cache rows only tried `us`; retry when more storefronts are available. */
export function shouldRetryAppStoreNotFoundLookup(
  lookupCountriesTried: string[] | undefined,
  requestedCountries: readonly string[],
): boolean {
  const tried = new Set(
    (lookupCountriesTried?.length ? lookupCountriesTried : ["us"]).map((c) =>
      c.trim().toLowerCase(),
    ),
  );
  return requestedCountries.some((c) => !tried.has(c.trim().toLowerCase()));
}
